"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Carousel = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = __importDefault(require("react"));
const react_native_1 = require("react-native");
const react_native_2 = require("react-native");
const react_native_fontawesome_1 = require("@fortawesome/react-native-fontawesome");
const Theme_1 = require("../Theme");
const ThemeContext_1 = require("../ThemeContext");
const styles = react_native_2.StyleSheet.create({
    rootContainer: {
        width: '100%',
    },
    carouselRow: {
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
    },
    overlayContainer: {
        position: 'relative',
        width: '100%',
    },
    viewport: {
        flex: 1,
        overflow: 'hidden',
    },
    track: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    itemWrapper: {
        flex: 1,
    },
    arrowButton: {
        width: 36,
        height: 36,
        borderRadius: 18,
        borderWidth: 1,
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.12,
        shadowRadius: 2,
        elevation: 2,
        ...(react_native_1.Platform.OS === 'web'
            ? {
                cursor: 'pointer',
                userSelect: 'none',
            }
            : {}),
    },
    arrowButtonDisabled: {
        opacity: 0.35,
        elevation: 0,
        shadowOpacity: 0,
    },
    arrowButtonPressed: {
        opacity: 0.75,
    },
    leftArrowMargin: {
        marginRight: 8,
    },
    rightArrowMargin: {
        marginLeft: 8,
    },
    overlayArrowLeft: {
        position: 'absolute',
        left: 8,
        top: '50%',
        marginTop: -18,
        zIndex: 10,
    },
    overlayArrowRight: {
        position: 'absolute',
        right: 8,
        top: '50%',
        marginTop: -18,
        zIndex: 10,
    },
    paginationContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 12,
    },
    paginationDot: {
        height: 8,
        borderRadius: 4,
        marginHorizontal: 3,
        ...(react_native_1.Platform.OS === 'web'
            ? {
                cursor: 'pointer',
            }
            : {}),
    },
});
function CarouselInner(props, ref) {
    const { data = [], renderItem, renderEmpty, keyExtractor, visibleItemsCount = 3, itemWidth: itemWidthProp, itemSpacing = 12, preventEmptySpace = false, initialIndex = 0, selectedIndex: controlledIndex, onIndexChange, onItemPress, selectOnItemPress = false, loop = false, animationDuration = 300, animationEasing = react_native_1.Easing.out(react_native_1.Easing.cubic), inactiveScale = 1, inactiveOpacity = 1, containerStyle, viewportStyle, trackStyle, itemContainerStyle, arrowPosition = 'sides', arrowContainerStyle, leftArrowContainerStyle, rightArrowContainerStyle, arrowColor, arrowDisabledColor, arrowSize = 16, renderLeftArrow, renderRightArrow, hideArrows = false, enableSwipe = true, showPagination = false, paginationContainerStyle, paginationActiveColor, paginationInactiveColor, } = props;
    const THEME = (0, ThemeContext_1.useTheme)();
    const [viewportWidth, setViewportWidth] = react_1.default.useState(0);
    const [hoveredLeft, setHoveredLeft] = react_1.default.useState(false);
    const [hoveredRight, setHoveredRight] = react_1.default.useState(false);
    const isEmpty = !data || data.length === 0;
    const itemsOnSide = itemWidthProp !== undefined && viewportWidth > 0
        ? (itemWidthProp + itemSpacing > 0
            ? Math.max(0, Math.ceil(((viewportWidth - itemWidthProp) / 2) / (itemWidthProp + itemSpacing)))
            : 0)
        : Math.max(0, Math.ceil((visibleItemsCount - 1) / 2));
    const maxPossible = Math.max(0, data.length - 1);
    const hasEnoughItems = itemsOnSide * 2 <= maxPossible;
    const minIndex = preventEmptySpace && hasEnoughItems ? Math.min(itemsOnSide, maxPossible) : 0;
    const maxIndex = preventEmptySpace && hasEnoughItems ? Math.max(0, maxPossible - itemsOnSide) : maxPossible;
    const isControlled = controlledIndex !== undefined;
    const initialClampedIndex = Math.max(minIndex, Math.min(initialIndex, maxIndex));
    const [internalIndex, setInternalIndex] = react_1.default.useState(initialClampedIndex);
    const activeIndex = isControlled
        ? Math.max(minIndex, Math.min(controlledIndex, maxIndex))
        : internalIndex;
    const activeIndexRef = react_1.default.useRef(activeIndex);
    activeIndexRef.current = activeIndex;
    const translateX = react_1.default.useRef(new react_native_1.Animated.Value(0)).current;
    // Calculate individual item width based on viewport width, visible count, and spacing
    const calculatedItemWidth = itemWidthProp ??
        (viewportWidth > 0
            ? Math.max(0, (viewportWidth - (visibleItemsCount - 1) * itemSpacing) / visibleItemsCount)
            : 0);
    // Centering calculation:
    // With item 0 starting at track position 0, the center of item i is i * (itemWidth + spacing) + itemWidth / 2.
    // To align the center of item i with the center of the viewport (viewportWidth / 2),
    // translateX must equal (viewportWidth - itemWidth) / 2 - i * (itemWidth + spacing).
    const getTranslateXForIndex = react_1.default.useCallback((index, vWidth = viewportWidth) => {
        if (vWidth <= 0)
            return 0;
        const iWidth = itemWidthProp ??
            Math.max(0, (vWidth - (visibleItemsCount - 1) * itemSpacing) / visibleItemsCount);
        return (vWidth - iWidth) / 2 - index * (iWidth + itemSpacing);
    }, [viewportWidth, itemWidthProp, visibleItemsCount, itemSpacing]);
    // Reposition track when viewport width or geometry changes
    const handleViewportLayout = react_1.default.useCallback((event) => {
        const width = event.nativeEvent.layout.width;
        if (width > 0 && Math.abs(width - viewportWidth) > 0.5) {
            setViewportWidth(width);
            const newX = getTranslateXForIndex(activeIndexRef.current, width);
            translateX.setValue(newX);
        }
    }, [viewportWidth, getTranslateXForIndex, translateX]);
    const animateToIndex = react_1.default.useCallback((targetIndex, animated = true) => {
        if (!data || data.length === 0)
            return;
        const clamped = Math.max(minIndex, Math.min(targetIndex, maxIndex));
        if (!isControlled)
            setInternalIndex(clamped);
        activeIndexRef.current = clamped;
        const targetX = getTranslateXForIndex(clamped);
        if (animated) {
            react_native_1.Animated.timing(translateX, {
                toValue: targetX,
                duration: animationDuration,
                easing: animationEasing,
                useNativeDriver: react_native_1.Platform.OS !== 'web',
            }).start();
        }
        else {
            translateX.setValue(targetX);
        }
        onIndexChange?.(clamped, data[clamped]);
    }, [
        data,
        minIndex,
        maxIndex,
        isControlled,
        getTranslateXForIndex,
        translateX,
        animationDuration,
        animationEasing,
        onIndexChange,
    ]);
    const handlePrev = react_1.default.useCallback(() => {
        if (!data || data.length === 0)
            return;
        const current = activeIndexRef.current;
        if (current > minIndex) {
            animateToIndex(current - 1, true);
        }
        else if (loop) {
            animateToIndex(maxIndex, true);
        }
    }, [data, loop, minIndex, maxIndex, animateToIndex]);
    const handleNext = react_1.default.useCallback(() => {
        if (!data || data.length === 0)
            return;
        const current = activeIndexRef.current;
        if (current < maxIndex) {
            animateToIndex(current + 1, true);
        }
        else if (loop) {
            animateToIndex(minIndex, true);
        }
    }, [data, loop, minIndex, maxIndex, animateToIndex]);
    // Keep activeIndexRef and animation in sync with controlled selectedIndex
    react_1.default.useEffect(() => {
        if (isControlled && controlledIndex !== undefined) {
            const clamped = Math.max(minIndex, Math.min(controlledIndex, maxIndex));
            if (clamped !== activeIndexRef.current) {
                animateToIndex(clamped, true);
            }
        }
    }, [isControlled, controlledIndex, minIndex, maxIndex, animateToIndex]);
    // Clamp uncontrolled active index if bounds shift dynamically
    react_1.default.useEffect(() => {
        if (preventEmptySpace && !isControlled) {
            const current = activeIndexRef.current;
            if (current < minIndex) {
                animateToIndex(minIndex, false);
            }
            else if (current > maxIndex) {
                animateToIndex(maxIndex, false);
            }
        }
    }, [preventEmptySpace, isControlled, minIndex, maxIndex, animateToIndex]);
    // Expose ref methods
    react_1.default.useImperativeHandle(ref, () => ({
        scrollToIndex: (index, animated = true) => {
            animateToIndex(index, animated);
        },
        scrollToNext: (animated = true) => {
            const current = activeIndexRef.current;
            if (current < maxIndex) {
                animateToIndex(current + 1, animated);
            }
            else if (loop) {
                animateToIndex(minIndex, animated);
            }
        },
        scrollToPrev: (animated = true) => {
            const current = activeIndexRef.current;
            if (current > minIndex) {
                animateToIndex(current - 1, animated);
            }
            else if (loop) {
                animateToIndex(maxIndex, animated);
            }
        },
        getCurrentIndex: () => activeIndexRef.current,
    }), [animateToIndex, minIndex, maxIndex, loop]);
    // PanResponder for touch / swipe gestures
    const handlePrevRef = react_1.default.useRef(handlePrev);
    handlePrevRef.current = handlePrev;
    const handleNextRef = react_1.default.useRef(handleNext);
    handleNextRef.current = handleNext;
    const panResponder = react_1.default.useRef(react_native_1.PanResponder.create({
        onStartShouldSetPanResponder: () => false,
        onMoveShouldSetPanResponder: (_, gestureState) => {
            if (!enableSwipe)
                return false;
            return (Math.abs(gestureState.dx) > 15 &&
                Math.abs(gestureState.dx) > Math.abs(gestureState.dy));
        },
        onPanResponderRelease: (_, gestureState) => {
            if (!enableSwipe)
                return;
            if (gestureState.dx > 40 || gestureState.vx > 0.5) {
                handlePrevRef.current();
            }
            else if (gestureState.dx < -40 || gestureState.vx < -0.5) {
                handleNextRef.current();
            }
        },
    })).current;
    // Web keyboard arrow navigation
    const handleKeyDown = react_1.default.useCallback((event) => {
        if (react_native_1.Platform.OS === 'web') {
            if (event.key === 'ArrowLeft') {
                event.preventDefault?.();
                handlePrev();
            }
            else if (event.key === 'ArrowRight') {
                event.preventDefault?.();
                handleNext();
            }
        }
    }, [handlePrev, handleNext]);
    const isLeftDisabled = isEmpty || (!loop && activeIndex <= minIndex);
    const isRightDisabled = isEmpty || (!loop && activeIndex >= maxIndex);
    const shouldInterpolate = (inactiveScale < 1 || inactiveOpacity < 1) &&
        calculatedItemWidth > 0 &&
        viewportWidth > 0;
    const renderArrowButton = (direction) => {
        const isLeft = direction === 'left';
        const disabled = isLeft ? isLeftDisabled : isRightDisabled;
        const isHovered = isLeft ? hoveredLeft : hoveredRight;
        const customRenderer = isLeft ? renderLeftArrow : renderRightArrow;
        const onPress = isLeft ? handlePrev : handleNext;
        if (customRenderer) {
            return customRenderer({ disabled, onPress, direction });
        }
        const icon = isLeft ? Theme_1.ICONS.Solid.ChevronLeft : Theme_1.ICONS.Solid.ChevronRight;
        const label = isLeft ? 'Previous slide' : 'Next slide';
        const arrowMargin = isLeft ? styles.leftArrowMargin : styles.rightArrowMargin;
        const customContainerStyle = isLeft
            ? leftArrowContainerStyle
            : rightArrowContainerStyle;
        const resolvedArrowColor = disabled
            ? (arrowDisabledColor ?? THEME.TextDisabled)
            : (arrowColor ?? THEME.Primary);
        return ((0, jsx_runtime_1.jsx)(react_native_2.Pressable, { accessibilityRole: "button", accessibilityLabel: label, accessibilityState: { disabled }, disabled: disabled, onPress: onPress, onPointerEnter: () => (isLeft ? setHoveredLeft(true) : setHoveredRight(true)), onPointerLeave: () => (isLeft ? setHoveredLeft(false) : setHoveredRight(false)), style: ({ pressed }) => [
                styles.arrowButton,
                arrowPosition === 'sides' ? arrowMargin : null,
                {
                    backgroundColor: isHovered && !disabled
                        ? (THEME.PrimaryLight ?? '#F0F3FF')
                        : (THEME.CardBackground ?? '#FFFFFF'),
                    borderColor: isHovered && !disabled
                        ? (THEME.Primary ?? '#5c688e')
                        : (THEME.CardBorder ?? '#D8D8D8'),
                },
                arrowContainerStyle,
                customContainerStyle,
                disabled ? styles.arrowButtonDisabled : null,
                pressed && !disabled ? styles.arrowButtonPressed : null,
            ], children: (0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { icon: icon, size: arrowSize, color: resolvedArrowColor, style: react_native_1.Platform.OS === 'web' ? { outlineWidth: 0 } : undefined }) }));
    };
    const renderCarouselItem = (item, index) => {
        const isSelected = index === activeIndex;
        const key = keyExtractor
            ? keyExtractor(item, index)
            : `carousel-item-${index}`;
        let itemTransform = undefined;
        let itemOpacity = undefined;
        if (shouldInterpolate) {
            const delta = calculatedItemWidth + itemSpacing;
            const centerTranslation = (viewportWidth - calculatedItemWidth) / 2 - index * delta;
            const inputRange = [
                centerTranslation - delta,
                centerTranslation,
                centerTranslation + delta,
            ];
            if (inactiveScale < 1) {
                const scale = translateX.interpolate({
                    inputRange,
                    outputRange: [inactiveScale, 1, inactiveScale],
                    extrapolate: 'clamp',
                });
                itemTransform = [{ scale }];
            }
            if (inactiveOpacity < 1) {
                itemOpacity = translateX.interpolate({
                    inputRange,
                    outputRange: [inactiveOpacity, 1, inactiveOpacity],
                    extrapolate: 'clamp',
                });
            }
        }
        const handlePress = () => {
            onItemPress?.(item, index);
            if (selectOnItemPress) {
                animateToIndex(index, true);
            }
        };
        const itemContent = renderItem({
            item,
            index,
            isSelected,
            itemWidth: calculatedItemWidth,
        });
        return ((0, jsx_runtime_1.jsx)(react_native_1.Animated.View, { style: [
                {
                    width: calculatedItemWidth,
                    marginRight: index < data.length - 1 ? itemSpacing : 0,
                },
                itemTransform ? { transform: itemTransform } : null,
                itemOpacity ? { opacity: itemOpacity } : null,
                itemContainerStyle,
            ], children: onItemPress || selectOnItemPress ? ((0, jsx_runtime_1.jsx)(react_native_2.Pressable, { onPress: handlePress, style: styles.itemWrapper, accessibilityRole: "button", children: itemContent })) : ((0, jsx_runtime_1.jsx)(react_native_2.View, { style: styles.itemWrapper, children: itemContent })) }, key));
    };
    const renderPagination = () => {
        if (!showPagination || data.length <= 1)
            return null;
        return ((0, jsx_runtime_1.jsx)(react_native_2.View, { style: [styles.paginationContainer, paginationContainerStyle], children: data.map((_, dotIndex) => {
                const isDotActive = dotIndex === activeIndex;
                return ((0, jsx_runtime_1.jsx)(react_native_2.Pressable, { accessibilityRole: "button", accessibilityLabel: `Go to slide ${dotIndex + 1}`, onPress: () => animateToIndex(dotIndex, true), style: [
                        styles.paginationDot,
                        {
                            backgroundColor: isDotActive
                                ? (paginationActiveColor ?? THEME.Primary)
                                : (paginationInactiveColor ??
                                    THEME.CardBorder ??
                                    '#D8D8D8'),
                            width: isDotActive ? 18 : 8,
                        },
                    ] }, `carousel-dot-${dotIndex}`));
            }) }));
    };
    const renderEmptyContent = () => {
        if (typeof renderEmpty === 'function') {
            return renderEmpty();
        }
        return renderEmpty ?? null;
    };
    if (isEmpty && !renderEmpty) {
        return (0, jsx_runtime_1.jsx)(react_native_2.View, { style: [styles.rootContainer, containerStyle] });
    }
    const isOverlay = arrowPosition === 'overlay';
    return ((0, jsx_runtime_1.jsxs)(react_native_2.View, { style: [styles.rootContainer, containerStyle], children: [isOverlay ? ((0, jsx_runtime_1.jsxs)(react_native_2.View, { style: styles.overlayContainer, children: [(0, jsx_runtime_1.jsx)(react_native_2.View, { style: [styles.viewport, viewportStyle], onLayout: handleViewportLayout, ...(react_native_1.Platform.OS === 'web'
                            ? { tabIndex: 0, onKeyDown: handleKeyDown }
                            : {}), children: isEmpty ? (renderEmptyContent()) : (viewportWidth > 0 && calculatedItemWidth > 0 && ((0, jsx_runtime_1.jsx)(react_native_1.Animated.View, { style: [
                                styles.track,
                                {
                                    transform: [{ translateX }],
                                },
                                trackStyle,
                            ], ...(enableSwipe ? panResponder.panHandlers : {}), children: data.map((item, index) => renderCarouselItem(item, index)) }))) }), !hideArrows && ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(react_native_2.View, { style: [
                                    styles.overlayArrowLeft,
                                    leftArrowContainerStyle,
                                ], children: renderArrowButton('left') }), (0, jsx_runtime_1.jsx)(react_native_2.View, { style: [
                                    styles.overlayArrowRight,
                                    rightArrowContainerStyle,
                                ], children: renderArrowButton('right') })] }))] })) : ((0, jsx_runtime_1.jsxs)(react_native_2.View, { style: styles.carouselRow, children: [!hideArrows && renderArrowButton('left'), (0, jsx_runtime_1.jsx)(react_native_2.View, { style: [styles.viewport, viewportStyle], onLayout: handleViewportLayout, ...(react_native_1.Platform.OS === 'web'
                            ? { tabIndex: 0, onKeyDown: handleKeyDown }
                            : {}), children: isEmpty ? (renderEmptyContent()) : (viewportWidth > 0 && calculatedItemWidth > 0 && ((0, jsx_runtime_1.jsx)(react_native_1.Animated.View, { style: [
                                styles.track,
                                {
                                    transform: [{ translateX }],
                                },
                                trackStyle,
                            ], ...(enableSwipe ? panResponder.panHandlers : {}), children: data.map((item, index) => renderCarouselItem(item, index)) }))) }), !hideArrows && renderArrowButton('right')] })), renderPagination()] }));
}
exports.Carousel = react_1.default.forwardRef(CarouselInner);
exports.default = exports.Carousel;
